import Component from 'vue-class-component';
import { Mixins, Prop } from 'vue-property-decorator';
import Moment from 'moment';
import KEYS from '@/values/keys';
import CalendarMixin from '@/mixins/common/calendar.mixin';
import { _SCHOOL_CALENDAR_STORE } from '@/tools/values/school-calendar.values';
import { WEEK_DAYS } from '@/values/general.values';
import EdSchoolCalendar from '@/core/components/ed-school-calendar/ed-school-calendar.component.vue';
import EdSubjectDayContentComponent from '@/courses/components/ed-subject-builder/ed-subject-day-content/ed-subject-day-content.component.vue';
import EdSubjectDayOptionsComponent from '../ed-subject-day-options/ed-subject-day-options.component.vue';
import { ALL_CONTENT_ROUTES } from '@/app/private/my-content/views/all/all.router';
import { BUILDER_EVENT_TYPES, BUILDER_CALENDAR_TYPES, BUILDER_RULES, BUILDER_DAY_OPTIONS } from '@/courses/values/courses.values';
import { ISchoolDayCalendarEventItem } from '@/tools/interfaces/school-day-calendar-event-item.interface';
import { SubjectBuilderLessonType, SubjectBuilderMoveType } from '@/courses/types/subject-builder.types';
import { Status } from '@/models/status.model';
import { IParamsCreateComponent } from '@/courses/interfaces/params-create-component.interface';
import EdMoveLessonsModal from '../ed-move-lessons-modal/ed-move-lessons-modal.component.vue';

@Component({
  components: {
    EdSchoolCalendar,
    EdSubjectDayContentComponent,
    EdSubjectDayOptionsComponent
  }
})
export default class EdSubjectCalendar extends Mixins(CalendarMixin) {

  /* Props */
  @Prop({ default: 'CALENDAR' }) calendarType: 'CALENDAR' | 'LESSONS';
  @Prop({ default: () => new Status() }) status: Status;
  @Prop({ default: '' }) coverImage: string;
  @Prop({ default: () => [] }) lessons: any[];
  @Prop({ default: () => [] }) specialDays: any[];
  @Prop({ default: () => [] }) plainWorkingDays: any[];
  @Prop({ default: '' }) startDateCalendar: string;
  @Prop({ default: '' }) endDateCalendar: string;
  @Prop({ default: () => {} }) schoolDay: any;
  @Prop({ default: 0 }) existFunLessons: number;
  @Prop({ default: () => {} }) subject: number;
  @Prop({ default: () => [] }) additionalLessons: any[];
  @Prop({ default: true }) hasWorkingDaysIcon: boolean;
  @Prop({ default: true }) canLessonsBeMoved: boolean;
  @Prop({ default: true }) canLessonsOpened: boolean;
  @Prop({ default: false }) canMoveLessons: boolean;
  @Prop({ default: () => [] }) objectsTagged: any[];
  @Prop({ default: '' }) automaticFolderType: string;
  @Prop({ default: () => [] }) componentsSelected: IParamsCreateComponent[];
  @Prop() templateIdFromQuickSubject: any;
  @Prop({ default: true }) readonly showEvents: boolean;

  /* Data */
  public config: {
    showSidebar: boolean,
    eventHeight: number,
    showTabs: boolean,
    showToday: boolean,
    showNavigator: boolean,
    showWorkingDays: boolean,
  } = {
    showSidebar: false,
    eventHeight: 20,
    showTabs: false,
    showToday: false,
    showNavigator: false,
    showWorkingDays: false,
  }; 
  public saving: boolean = false;
  public calendarTypes: any = BUILDER_CALENDAR_TYPES;
  public eventTypes: any = BUILDER_EVENT_TYPES;
  public isEditionMode = false;
  public activateBottomNavigation = false;
  public targetLessonSelected: any = null;
  public srcLessonSelected: any = null;
  public moveType: SubjectBuilderMoveType = null;
  public lessonTypeSelected: SubjectBuilderLessonType = null;
  public changeSelected = null;
  public prevLessonDates = null;

  /* Computed */
  get isCalendarStep() {
    return this.calendarType == this.calendarTypes.CALENDAR;
  }

  get isLessonStep() {
    return this.calendarType == this.calendarTypes.LESSONS;
  }

  get workingDaysLabel () {
    return this.$t('courses.subjectBuilder.subjectTeachingDays');
  }

  get templateLessons() {
    if (this.isLessonStep) {
      return this.lessons;
    }
    return;
  }

  get funSpecialDays() {
    return this.specialDays.filter(el => el.status == BUILDER_EVENT_TYPES.FUN) || [];
  }

  get calendarObjects(): ISchoolDayCalendarEventItem[] {
    let addFunDays = [];
    if(this.isLessonStep && this.existFunLessons < this.funSpecialDays?.length) {
      this.funSpecialDays?.forEach(fl => {
        if(this.templateLessons && !this.templateLessons.find(tl => fl.startDate === tl.schoolDate)) {
          addFunDays.push(fl)
        } 
      })
    }
    return this.isCalendarStep ? this.getFunCalendarFormat(this.funSpecialDays) : this.getCalendarFormat(this.templateLessons).concat(this.getFunCalendarFormat(addFunDays))
  }
  
  get templateId (): number {
    return Number(this.$route.params.id);
  }

  get workingDays() {
    let days = [];
    for (let dayOfWeek of WEEK_DAYS) {
      days.push({
        label: dayOfWeek,
        checked: this.plainWorkingDays?.includes(dayOfWeek.toUpperCase())
      });
    }
    return days;
  }

  get organizationId() {
    let institutions: any[] = this.$store.state.ToolsStore.institutionInfo;
    return institutions.length > 0 ? institutions[0].organizationId : null;
  }

  get weekdays() {
    let workingDays = this.workingDays;
    let weekdays = [];
    workingDays.forEach((wd, i) => {
      if(wd.checked) weekdays.push(i);
    });
    return weekdays;
  }

  get startYear() : number {
    let startYear = 0;
    let startMonth = Number(Moment(this.schoolDay?.startDate).format('MM'));
    if(this.schoolDay){
      startYear = parseInt(this.schoolDay.startDate?.substr(0, 4));
    }
    if(startMonth < 8) startYear -= 1;
    return startYear;
  }

  get calendarState() {
    return {
      templateId: this.templateId, 
      schoolDay: this.schoolDay, 
      workingDays: this.plainWorkingDays,
      specialDays: this.specialDays,
      subject: this.subject,
      lessons: this.lessons.filter(el => !el.labelSections),
      objectsTagged: this.objectsTagged,
      automaticFolderType: this.automaticFolderType,
      componentsSelected: this.componentsSelected
    }
  }

  isSchoolDay(date: string): boolean {
    return this.getIsSchoolDay(date, [this.schoolDay]);
  }

  onWcCalendarDaySelection(customEvent: CustomEvent): void {
    const date = customEvent?.detail?.value;
    if (!date) {
      return;
    }

    return this.isPastDate(date) && (this.isLessonStep ? this.showDayContentModal(date) : this.showDayOptionsModal(date));
  }

  onWcCalendarEventMouseOver(customEvent: CustomEvent): void {
    const eventData = customEvent?.detail?.value?.eventData;
    const mouseEvent: MouseEvent = customEvent?.detail?.value?.mouseEvent;

    if (!eventData || !mouseEvent) {
      return;
    }

    this.showEdContextMenu(eventData.label, {x: mouseEvent.x, y: mouseEvent.y});
  }

  onWcCalendarEventMouseLeave(customEvent: CustomEvent): void {
    this.closeEdContextMenu();
  }

  onWcCalendarDayButtonMouseOver(customEvent: CustomEvent): void {
    const date = customEvent?.detail?.value?.date;
    const mouseEvent: MouseEvent = customEvent?.detail?.value?.mouseEvent;

    if (!date || !mouseEvent) return;

    if (this.isSchoolDay(date)) {
      this.showEdContextMenu(
        `${this.$t('tools.schooldays.schoolDay')}: ${this.getSchoolDay(date, [this.schoolDay]).day + 1}`,
        {x: mouseEvent.x, y: mouseEvent.y}
      );
    }
  }

  onWcCalendarDayButtonMouseLeave(customEvent: CustomEvent): void {
    this.closeEdContextMenu();
  }

  onWcCalendarEventDragStart(customEvent: CustomEvent): void {
    this.closeEdContextMenu();
  }

  getCalendarFormat(sections): ISchoolDayCalendarEventItem[] {
    let filtered: ISchoolDayCalendarEventItem[] = [];

    sections.forEach(el => {
      if (el.fixedSection === 'LESSON' && el.schoolDate) {
        let label = 'contentLesson';
        if(el.rootType == BUILDER_EVENT_TYPES.FILLER) label = 'fillerLesson';
        else if(el.rootType == BUILDER_EVENT_TYPES.FUN) label = 'funLesson';
        filtered.push({
          category: el.rootType,
          className: `event-${el.rootType.toLocaleLowerCase()}`,
          color: null,
          end: el.schoolDate,
          id: el.id,
          label: `${this.$t(`courses.subjectBuilder.${label}`)} ${el.fixedSequence}`,
          name: el.createAsNew ? `${el.name} ${el.fixedSequence}` : el.name,
          official: false,
          start: el.schoolDate,
          status: undefined,
          timed: false,
          parentId: el.parentId,
          type: el.rootType,
          subType: el.fixedSection,
        });
      } else if (el.sections?.length > 0) { 
        filtered = [...filtered, ...this.getCalendarFormat(el.sections)];
      }
    });

    return filtered;
  }

  getFunCalendarFormat(lessons): ISchoolDayCalendarEventItem[] {
    let filtered: ISchoolDayCalendarEventItem[] = [];

    lessons.forEach(el => {
      filtered.push({
        category: el.status,
        className: `event-${el.status.toLocaleLowerCase()}`,
        color: null,
        end: el.endDate,
        id: el.holidayPlanId,
        label: `${this.$i18n.t('courses.subjectBuilder.funDay')}`,
        name: `${this.$i18n.t('courses.subjectBuilder.funDay')}`,
        official: false,
        start: el.startDate,
        status: undefined,
        timed: false,
        type: el.status,
      });
    })

    return filtered;
  }

  goToPresentDay() {
    setTimeout(() => {
      let present: any = document.querySelector('.v-calendar-weekly__day.v-present');
      if(present) {
        let top = present.offsetTop - 300;
        document.getElementById('subjectBuilder').scrollTop = top;
      }
    }, 500);
  }

  showDayContentModal (date) {
    const lesson = this.templateLessons.find(el => el.schoolDate == date);
    if(lesson) {
      this.$EdModal.createModal(
        EdSubjectDayContentComponent,
        { 
          courseId: this.templateIdFromQuickSubject || this.templateId,
          date: date,
          lesson: lesson,
          objectsTagged: this.objectsTagged
        }
      );
    }
  }

  showDayOptionsModal(date) {
    this.$EdModal.createModal(
      EdSubjectDayOptionsComponent,
      { 
        date: date,
        setSpecialDays: this.setSpecialDays,
        specialDays: this.specialDays,
        existFunLessons: this.existFunLessons,
        lessons: this.lessons,
        schoolDay: this.schoolDay
      }
    );
  }

  goToTemplates() {
    this.$router.push({ name: ALL_CONTENT_ROUTES.TEMPLATES.NAME });
  }

  async saveTeachingDays(data) {
    this.$emit('onSaveTeachingDays', data);
  }

  async setSpecialDays(specialDays, rule, option = null, prevOption = null, date = '') {
    this.$emit('onSetSpecialDays', { specialDays, rule, option, prevOption, date });
  }

  isPastDate(date) {
    let todayDate = Moment(new Date(), 'DD-MM-YYYY');
    let pastDate = Moment(Moment(date), 'DD-MM-YYYY');
    return todayDate.diff(pastDate) > 0 ? false : true;
  }

  moveLessons(changeSelected = 0) {
    const isFutureMoved = this.moveType === 'future';

    let rule, specialDays;
  
    switch (changeSelected) {
      case 1:
        rule = BUILDER_RULES.SWAP_LESSONS
        break;
      case 2: 
        rule = isFutureMoved ? BUILDER_RULES.SHIFT_OF_THE_FEATURE : BUILDER_RULES.SHIFT_DAY_BACK;
        break;
      case 3: 
        rule = isFutureMoved ? BUILDER_RULES.SHIFT_OF_THE_FEATURE_WITHOUT_USE_LESSON : BUILDER_RULES.SHIFT_DAY_BACK_WITH_DAY_OFF;
        break;
      default: 
        rule = BUILDER_RULES.MOVE_LESSON_WHEREVER;
    }

    if (rule == BUILDER_RULES.SHIFT_DAY_BACK_WITH_DAY_OFF) {
      specialDays = this.specialDays.concat({
        endDate: this.prevLessonDates.schoolDate,
        holidayPlanId: null,
        name: this.$t('courses.subjectBuilder.dayOff'),
        official: false,
        startDate: this.prevLessonDates.schoolDate,
        status: BUILDER_DAY_OPTIONS.OBSERVED,
        addedByRule: true
      });
    } else {
      specialDays = this.specialDays;
    }

    rule = { 
      type: rule,
      lessonSourceId: this.srcLessonSelected.id,
      lessonTargetId: this.targetLessonSelected?.id,
      lessonDate: !this.targetLessonSelected?.id ? this.targetLessonSelected : undefined
    };

    const srcLessonFound = this.lessons.find(l => l.id === this.srcLessonSelected.id);
    srcLessonFound.schoolDate = this.prevLessonDates.schoolDate;

    this.setSpecialDays(specialDays, rule);
    this.initMoveValues();
  }

  initMoveValues() {
    this.changeSelected = null;
    this.srcLessonSelected = null;
    this.targetLessonSelected = null;
    this.prevLessonDates = null;
  }

  async refreshSubjectCalendar () {
    this.$emit('onRefreshSubjectCalendar');
  }

  onClickEvent(date) {
    if (!this.isPastDate(date)) {
      return;
    }
  
    if(this.canLessonsOpened) {
      if (this.isLessonStep) {
        this.showDayContentModal(date); 
      } else {
        this.showDayOptionsModal(date);
      }
    }
  }

  refreshCalendar() {
    this.status.setLoading();
    (this.$refs.schoolCalendar as any)?.createCalendar();
    setTimeout(() => {
      this.status.setLoad();
    }, 100)
  }

  onCancelMovement() {
    this.$emit('onUpdateLessonDates', this.prevLessonDates);
    this.initMoveValues();
  }

  onLessonMoved({ event, from, to, startSchoolCalendarDay, endSchoolCalendarDay }) {
    const srcLessonFound = this.lessons.find(l => l.id === event.id);
    const trgLessonFound = this.lessons.find(l => l.schoolDate === to);

    if(!srcLessonFound) { 
      return;
    }

    const prevDate = Moment(event.start).format('YYYY-MM-DD');
    this.prevLessonDates = {
      id: event.id,
      schoolDate: prevDate,
    };
    srcLessonFound.schoolDate = to;
    this.srcLessonSelected = srcLessonFound;

    if(srcLessonFound && (!trgLessonFound || trgLessonFound.labelSections)) {
      
      // Move to a date with no lesson
      const isTargetDayOff = this.specialDays.find(el => el.startDate == to && el.status == BUILDER_DAY_OPTIONS.OBSERVED);
      const cantMoveLessonToFun = this.specialDays.find(el => el.startDate == to && el.status == BUILDER_DAY_OPTIONS.FUN) && srcLessonFound.rootType !== BUILDER_DAY_OPTIONS.FUN;
      const cantMoveFunToAny = !this.specialDays.find(el => el.startDate == to && el.status == BUILDER_DAY_OPTIONS.FUN) && srcLessonFound.rootType == BUILDER_DAY_OPTIONS.FUN;
      
      let startCalendar = Moment(startSchoolCalendarDay);
      if(Moment(startSchoolCalendarDay).isBefore(Moment())) 
        startCalendar = Moment();

      if(Moment(to).isSameOrAfter(startCalendar) && Moment(to).isSameOrBefore(Moment(endSchoolCalendarDay)) && !isTargetDayOff && !cantMoveLessonToFun && !cantMoveFunToAny) {
        this.targetLessonSelected = to;
        this.moveLessons();
      } else {
        this.srcLessonSelected.schoolDate = from;
        if(isTargetDayOff) {
          this.$EdModal.createMessageModal({
            title: this.$i18n.t('courses.subjectBuilder.errors.movementForbiddenTitle'),
            body: this.$i18n.t('courses.subjectBuilder.errors.errorMovingToDayOff'),
            type: KEYS.EVENT_TYPES.WARNING
          });
        } else if(cantMoveFunToAny || cantMoveLessonToFun) {
          this.$EdModal.createMessageModal({
            title: this.$i18n.t('courses.subjectBuilder.errors.movementForbiddenTitle'),
            body: this.$i18n.t('courses.subjectBuilder.errors.errorMovingFunToAny'),
            type: KEYS.EVENT_TYPES.WARNING
          });
        }
      }

    } else {
      // Move to a date with lesson
      if (((srcLessonFound.rootType == BUILDER_EVENT_TYPES.FILLER || srcLessonFound.rootType == BUILDER_EVENT_TYPES.CONTENT) &&
        (trgLessonFound.rootType == BUILDER_EVENT_TYPES.FILLER || trgLessonFound.rootType == BUILDER_EVENT_TYPES.CONTENT)) || 
        (srcLessonFound.rootType == BUILDER_EVENT_TYPES.FUN && trgLessonFound.rootType == BUILDER_EVENT_TYPES.FUN)) {

        this.targetLessonSelected = trgLessonFound;
        this.moveType = Moment(from).isBefore(Moment(to)) ? 'back' : 'future';

        this.$EdModal.createModal(EdMoveLessonsModal, {
          targetLessonSelected: this.targetLessonSelected,
          srcLessonSelected: this.srcLessonSelected,
          prevLessonDates: this.prevLessonDates,
          moveType: this.moveType,
          moveLessons: this.moveLessons,
          onCancelMovement: this.onCancelMovement
        })
      } else {
        srcLessonFound.schoolDate = from;
        this.$EdModal.createMessageModal({
          title: this.$i18n.t('courses.subjectBuilder.errors.movementForbiddenTitle'),
          body: this.$i18n.t('courses.subjectBuilder.errors.errorMovingLesson'),
          type: KEYS.EVENT_TYPES.WARNING
        });
      }
    }
  }

  movingLessons({ mouse, dragTime, dragEvent, startSchoolCalendarDay, endSchoolCalendarDay }) {
    let newStartTime;

    // Prevent moving Forbidden objects
    if(dragEvent.subType !== BUILDER_EVENT_TYPES.LESSON)
      return

    newStartTime = mouse - dragTime;
    const newStartTimeFormat = Moment(newStartTime).format('YYYY-MM-DD');
    let startCalendar = Moment(startSchoolCalendarDay);

    if(Moment(startSchoolCalendarDay).isBefore(Moment())) 
      startCalendar = Moment();

    if(Moment(newStartTimeFormat).isSameOrAfter(startCalendar) 
      && Moment(newStartTimeFormat).isSameOrBefore(Moment(endSchoolCalendarDay))) {
        return newStartTime
    }
  }

  updated() {
    (this.$refs.schoolCalendar as any).createCalendar();
  }

}